import React from 'react';
import Link from 'next/link';
import { useParams } from 'next/navigation';
import round from 'lodash/round';
import cloneDeep from 'lodash/cloneDeep';
import GeneralChart from '@/components/job-title/GeneralChart';
import { Liquid } from '@/components/AsyncComponent';
import DataInterpretationSEO from '@/components/DataInterpretationSEO';
import { DataTableColumnsType } from '@/components/job-title/DataTable';
import { JobTitleContext } from '@/context/create-context';
import { TAB_CITY_COMPARISON } from '@/constants/job-title';
import { trackClickEvent } from '@/utils/site-track';
import { PRODUCTION_HOST } from '@/constants/router-path';

interface Props {
  title: string;
  size?: number;
  pinned?: CityRankRowType;
  footer?: React.ReactNode;
  displayName?: string;
}

const columns: DataTableColumnsType = [
  {
    headerName: '城市',
    field: 'name',
    align: 'left',
    sortable: false,
    renderCell: (name, row, isPinned) => {
      const number = row?.id as number;
      const { pinyin, jobTitleId } = row;
      const url = `/${pinyin}/${jobTitleId}/`;

      return (
        <div className="flex items-center -ml-5">
          <span
            className={`font-bold text-base ${
              number <= 3 ? 'text-[#c2b105]' : ''
            } mr-1 ${isPinned ? 'text-1.5xl' : ''} tablet:mr-2.5`}
          >
            {number}
          </span>
          <Link
            href={url}
            className="underline city-link"
            onClick={(e) => trackClickEvent(e, true)}
            data-ele-group={`${TAB_CITY_COMPARISON}-comparison_city-link`}
            data-ele-id={`city-link_${name}`}
          >
            {name}
          </Link>
        </div>
      );
    },
  },
  {
    headerName: '职位数',
    field: 'count',
    align: 'center',
    sortable: false,
  },
  {
    headerName: '平均月薪',
    field: 'avg',
    align: 'center',
    sortable: false,
    renderCell: (data) => {
      return '￥' + round(data, -2);
    },
  },
  {
    headerName: '城市平均房价',
    field: 'housePrice',
    align: 'center',
    headerAlign: 'center',
    sortable: false,
    renderCell: (data) => {
      return '￥' + data;
    },
  },
  {
    headerName: '谈职薪资竞争力指数',
    field: 'competitive',
    align: 'center',
    sortable: false,
    flex: 1,
    renderCell: (data) => {
      let value = 0;
      if (typeof data === 'number') value = Math.round(data);
      return <Liquid value={value} />;
    },
  },
];

export type CityRankRowType = {
  id: number;
  name: string;
  count: number;
  avg: number;
  housePrice: number;
  competitive: number;
  isPinned?: boolean;
};

const CityRankTable = (props: Props) => {
  const { title, footer, pinned, size = 10 } = props;
  const {
    jobTitle: { locations },
    displayName,
  } = React.useContext(JobTitleContext);
  const params = useParams();

  const [showMedian, setShowMedian] = React.useState(false);

  const getDegreeWord = (index: number) => {
    let word = '';
    switch (index) {
      case 0:
        word = '最高';
        break;
      case 1:
      case 2:
        word = '适中';
        break;
      case 3:
      case 4:
        word = '较低';
        break;
      default:
        break;
    }
    return word;
  };

  const rankData = React.useMemo(() => {
    const rows = locations.slice(0, size).map((item) => ({
      id: item.competitive_rank,
      name: item.name,
      pinyin: item.pinyin,
      jobTitleId: params?.id,
      count: item.job_open_count,
      avg: item.avg,
      avgMedian: item.avg_median,
      housePrice: item.house_price,
      competitive: item.competitive_rate * 100,
    }));
    const sortData = locations.slice(0, 5);
    const firstLocationName = sortData[0]?.name;
    const secondLocationName = sortData[1]?.name;
    const firstMonthlySalaryData = cloneDeep(sortData)
      .sort((a, b) => a.avg - b.avg)
      .map((item) => item.name);
    const firstMonthlySalaryIndex =
      firstMonthlySalaryData.indexOf(firstLocationName);
    const secondMonthlySalaryIndex =
      firstMonthlySalaryData.indexOf(secondLocationName);
    const jobsCountData = cloneDeep(sortData)
      .sort((a, b) => b.job_open_count - a.job_open_count)
      .map((item) => item.name);
    const firstJobsCountIndex = jobsCountData.indexOf(firstLocationName);
    const secondJobsCountIndex = jobsCountData.indexOf(secondLocationName);
    const housePriceData = cloneDeep(sortData)
      .sort((a, b) => a.house_price - b.house_price)
      .map((item) => item.name);
    const firstHousePriceIndex = housePriceData.indexOf(firstLocationName);
    const secondHousePriceIndex = housePriceData.indexOf(secondLocationName);

    return {
      rows,
      firstLocation: {
        name: firstLocationName,
        monthlySalary: getDegreeWord(firstMonthlySalaryIndex),
        housePrice: getDegreeWord(firstHousePriceIndex),
        jobsCount: getDegreeWord(firstJobsCountIndex),
        monthlySalaryRate:
          firstMonthlySalaryIndex > secondMonthlySalaryIndex ? '高于' : '低于',
        housePriceRate:
          firstHousePriceIndex > secondHousePriceIndex ? '高于' : '低于',
      },
      secondLocationName,
      jobsCountRateName:
        firstJobsCountIndex < secondJobsCountIndex ||
        secondJobsCountIndex === -1
          ? firstLocationName
          : secondLocationName,
    };
  }, []);

  const { rows, firstLocation, secondLocationName, jobsCountRateName } =
    rankData;

  React.useEffect(() => {
    setShowMedian(window.location.host !== PRODUCTION_HOST);
  }, []);

  return (
    <GeneralChart
      type="dataTable"
      title={title}
      footer={locations.length > 5 ? footer : null}
      config={{
        headerHeight: 70,
        columns: !showMedian
          ? columns
          : [
              ...columns.slice(0, 3),
              {
                headerName: '平均中位',
                field: 'avgMedian',
                align: 'center',
                sortable: false,
                renderCell: (data: any) => {
                  return '￥' + round(data || 0, -2);
                },
              },
              ...columns.slice(3),
            ],
        rows: pinned
          ? [pinned, ...rows.filter((item) => item.name !== pinned.name)]
          : rows,
        tableRootClass: 'py-2',
      }}
      isCityRankTable={true}
      seoInfo={
        locations?.length > 0 ? (
          <DataInterpretationSEO
            lineClamp="line-clamp-0"
            className="font-normal text-opacity-85"
          >
            {`${displayName}在全国哪个城市的需求量最高？${
              secondLocationName
                ? `去${firstLocation.name}工作好还是去${secondLocationName}工作好？`
                : `只在${firstLocation.name}招聘。`
            }${displayName}在${jobsCountRateName}的需求量最高，${
              secondLocationName
                ? `${
                    jobsCountRateName === firstLocation.name
                      ? secondLocationName
                      : firstLocation.name
                  }其次，${
                    jobsCountRateName === firstLocation.name ? '' : '但'
                  }${firstLocation.name}平均房价${
                    firstLocation.housePriceRate
                  }${secondLocationName}，平均月薪${
                    firstLocation.monthlySalaryRate
                  }${secondLocationName}，整体的竞争力指数${
                    firstLocation.name
                  }更高`
                : `${firstLocation.name}平均月薪${firstLocation.monthlySalary}，平均房价${firstLocation.housePrice}，职位数量${firstLocation.jobsCount}，整体竞争力在全国排名第一。`
            }。本数据统计来源于各大平台发布的公开数据，竞争力指数为平台自行分析得出，仅供参考。`}
          </DataInterpretationSEO>
        ) : null
      }
    />
  );
};

export default CityRankTable;
